import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { ClientsTable } from '../../components/admin/DataTables';
import { ClientProfile } from '../../components/admin/Profiles';
import { KPICard, KPIGrid } from '../../components/admin/KPICards';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Users, UserPlus, Award, Gift, Plus, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminClients() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  
  // New referral modal state
  const [showNewReferral, setShowNewReferral] = useState(false);
  const [newReferralClient, setNewReferralClient] = useState(null);
  const [newReferralName, setNewReferralName] = useState('');
  const [newReferralEmail, setNewReferralEmail] = useState('');
  const [creatingReferral, setCreatingReferral] = useState(false);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/clients`, { 
        withCredentials: true 
      });
      setClients(response.data);
      
      // Check if we should auto-select a client from URL params
      const clientId = searchParams.get('id');
      if (clientId) {
        const client = response.data.find(c => c.id === clientId);
        if (client) setSelectedClient(client);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleViewClient = (client) => {
    setSelectedClient(client);
  };

  const handleAddReferral = (client) => {
    setNewReferralClient(client);
    setShowNewReferral(true);
  };

  const handleCreateReferral = async () => {
    if (!newReferralClient || !newReferralName.trim()) return;
    
    setCreatingReferral(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/referrals/create`,
        {
          referrer_id: newReferralClient.id,
          name: newReferralName.trim(),
          email: newReferralEmail.trim() || null,
        },
        { withCredentials: true }
      );
      setShowNewReferral(false);
      setNewReferralName('');
      setNewReferralEmail('');
      setNewReferralClient(null);
      await fetchClients();
    } catch (err) {
      console.error('Error creating referral:', err);
    } finally {
      setCreatingReferral(false);
    }
  };

  // Calculate stats
  const totalClients = clients.length;
  const totalReferrals = clients.reduce((acc, c) => acc + c.total_referrals, 0);
  const totalValidated = clients.reduce((acc, c) => acc + c.validated_referrals, 0);
  const totalRewards = clients.reduce((acc, c) => acc + c.available_rewards, 0);

  if (loading) {
    return (
      <AdminLayout
        title="Clientes"
        subtitle="Cargando..."
        onLogout={handleLogout}
      >
        <div className="flex items-center justify-center h-96">
          <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Clientes"
      subtitle={`${totalClients} registrados`}
      pendingRewards={totalRewards}
      onLogout={handleLogout}
    >
      {/* Stats */}
      <KPIGrid className="mb-4 lg:mb-8">
        <KPICard
          title="Clientes"
          value={totalClients}
          icon={Users}
        />
        <KPICard
          title="Referidos"
          value={totalReferrals}
          icon={UserPlus}
        />
        <KPICard
          title="Validados"
          value={totalValidated}
          icon={Award}
        />
        <KPICard
          title="Recompensas"
          value={totalRewards}
          icon={Gift}
          variant={totalRewards > 0 ? 'accent' : 'default'}
        />
      </KPIGrid>

      {/* Search */}
      <div className="mb-4 lg:mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 lg:w-5 lg:h-5 text-zinc-500" />
          <Input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar..."
            className="pl-10 lg:pl-11 h-10 lg:h-12 bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600 focus:border-white/30 rounded-lg text-sm"
            data-testid="search-clients-input"
          />
        </div>
      </div>

      {/* Clients Table */}
      <ClientsTable
        clients={clients}
        searchTerm={searchTerm}
        onViewClient={handleViewClient}
        onAddReferral={handleAddReferral}
      />

      {/* Client Profile Sidebar */}
      {selectedClient && (
        <ClientProfile
          client={selectedClient}
          onClose={() => setSelectedClient(null)}
          onAddReferral={handleAddReferral}
        />
      )}

      {/* New Referral Modal */}
      <Dialog open={showNewReferral} onOpenChange={setShowNewReferral}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit']">
              Agregar Referido
            </DialogTitle>
          </DialogHeader>
          
          {newReferralClient && (
            <div className="py-2">
              <p className="text-sm text-zinc-400 mb-4">
                Para el cliente: <span className="text-white">{newReferralClient.name}</span>
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">
                    Nombre del referido *
                  </label>
                  <Input
                    value={newReferralName}
                    onChange={(e) => setNewReferralName(e.target.value)}
                    placeholder="Nombre completo"
                    className="h-12 bg-[#121212] border-white/10 text-white placeholder:text-zinc-600 focus:border-white/30 rounded-lg"
                    data-testid="new-referral-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">
                    Email (opcional)
                  </label>
                  <Input
                    type="email"
                    value={newReferralEmail}
                    onChange={(e) => setNewReferralEmail(e.target.value)}
                    placeholder="email@ejemplo.com"
                    className="h-12 bg-[#121212] border-white/10 text-white placeholder:text-zinc-600 focus:border-white/30 rounded-lg"
                    data-testid="new-referral-email-input"
                  />
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-3">
            <Button
              variant="ghost"
              onClick={() => setShowNewReferral(false)}
              className="text-zinc-400 hover:text-white hover:bg-white/5"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateReferral}
              disabled={!newReferralName.trim() || creatingReferral}
              className="bg-white text-black hover:bg-zinc-200 rounded-lg"
              data-testid="create-referral-submit-btn"
            >
              {creatingReferral ? 'Creando...' : 'Crear Referido'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
